/**
 *
 * @alias components.core.AppUrlsPlugin
 * @classdesc Manages in-game urls, allowing you to use safely convert game relative urls to absolute urls
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.appUrls
 * </pre>
 *
 * The default paths can be overridden on the [getConfig()]{@link getConfigBase} component options
 * <pre>
 * components : {
 *  appUrls : { data : {  base, assets, fonts } }
 * }
 * </pre>
 *
 * Or by calling the [setPaths]{@link components.core.AppUrlsPlugin#setPaths} method when ready
 *
 * <pre>
 * this.game.appUrl.setPaths( { base, assets, fonts } )
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', AppUrlsPlugin, true, 'scenekey' || null, { base, assets, fonts } );
 * </pre>
 *
 */
class AppUrlsPlugin extends Phaser.Plugins.BasePlugin {
  constructor(pluginManager) {
    super(pluginManager);

    this._basePath = './';
    this._assetsPath = 'assets/';
    this._fontsPath = 'fonts/';
  }

  init(config) {
    config = config || {};
    this._basePath = config.base || this._basePath;
    this._assetsPath = config.assets || this._assetsPath;
    this._fontsPath = config.fontsPath || this._fontsPath;
    this.game.config.loaderBaseURL = this._basePath;
  }

  /**
   * Set the base/assets and fonts paths for component to use when requesting directories
   * @param {Object} path
   * @param {string} [path.base='./']  url to the game root
   * @param {string} [path.assets='assets/']  the relative path to the assets root
   * @param {string} [path.fonts='fonts/']  the relative path to the fonts root
   *
   */
  setPaths(paths = {}) {
    this._basePath = paths.base || this._basePath;
    this._assetsPath = paths.assets || this._assetsPath;
    this._fontsPath = paths.fonts || this._fontsPath;
    this.game.config.loaderBaseURL = this._basePath;
  }

  /**
   * Set the absolute path to the root folder
   * @param {stiring} path The url to the game root
   */
  setBasePath(path = './') {
    this._basePath = path || this._basePath;
  }

  /**
   * Set the relative path to the games asset folder
   * @param {stiring} path the relative path to the assets root
   */
  setAssetsPath(path = 'assets/') {
    this._assetsPath = path || this._assetsPath;
  }

  /**
   * Set the relative path to the games font folder
   * @param {stiring} path the relative path to the font root
   */
  setFontsPath(path = 'fonts/') {
    this._fontsPath = path || this._fontsPath;
  }

  /**
   * Get the root directory for the game
   * @returns {string}
   */
  getBaseDirectory() {
    return this._basePath;
  }

  /**
   * Get the absolute url to a supplied relative game path
   * @param {string} path a path relative to game root directory
   * @returns {string}
   */
  resolveRelativeUrl(path) {
    return this.getBaseDirectory() + path;
  }

  /**
   * Get the absolute url to the assets directory, also accepts a suffix which can be appended
   * @param {string} [urlSuffix] a path relative to the assets directory
   * @returns {string}
   */
  getAssetsDirectory(urlSuffix = '') {
    return this.resolveRelativeUrl(this._assetsPath + urlSuffix);
  }

  /**
   * Get the absolute url the fonts directory, also accepts a suffix which can be appended
   * @param {string} [urlSuffix] a path relative to the fonts directory
   * @returns {string}
   */
  getFontsDirectory(urlSuffix) {
    return this.getAssetsDirectory(this._fontsPath + urlSuffix);
  }
}

export { AppUrlsPlugin };
